import React, {useState} from 'react';
import {Search, Filter} from 'lucide-react';
import {Input} from './Input';
import {Button} from './Button';
import {Select, type SelectOption} from './Select';

export interface FilterBarSelect {
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    placeholder?: string;
}

export interface FilterBarProps {
    searchValue: string;
    onSearchChange: (value: string) => void;
    searchPlaceholder?: string;
    /** Dropdown filters shown in the collapsible panel behind the "Filters" button. */
    filters?: FilterBarSelect[];
    /** Grid columns for the expanded panel. Defaults to the filter count (capped at 4). */
    columns?: 1 | 2 | 3 | 4;
}

const GRID_COLS: Record<number, string> = {
    1: 'md:grid-cols-1',
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
};

/**
 * Shared search + collapsible dropdown-filter bar used across the admin list
 * pages (hosts, accounts, organizations). The parent owns the filter state and
 * query wiring; this component only renders the controls and owns the
 * show/hide of the panel.
 */
export const FilterBar: React.FC<FilterBarProps> = ({
                                                        searchValue,
                                                        onSearchChange,
                                                        searchPlaceholder = 'Search...',
                                                        filters = [],
                                                        columns,
                                                    }) => {
    const [showFilters, setShowFilters] = useState(false);
    const cols = GRID_COLS[Math.min(columns ?? filters.length, 4) || 1];

    return (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center space-x-4">
                <div className="flex-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-gray-400"/>
                    </div>
                    <Input
                        type="text"
                        placeholder={searchPlaceholder}
                        value={searchValue}
                        onChange={(e) => onSearchChange(e.target.value)}
                        className="w-full pl-10"
                    />
                </div>
                {filters.length > 0 && (
                    <Button
                        variant="secondary"
                        onClick={() => setShowFilters((v) => !v)}
                        className="flex items-center space-x-2"
                    >
                        <Filter className="h-4 w-4"/>
                        <span>Filters</span>
                    </Button>
                )}
            </div>

            {showFilters && filters.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className={`grid grid-cols-1 ${cols} gap-4`}>
                        {filters.map((f, i) => (
                            <Select
                                key={i}
                                value={f.value}
                                onChange={f.onChange}
                                options={f.options}
                                placeholder={f.placeholder}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
